import { useEffect, useState } from 'react';
import styles from './Tabs.module.scss';
import { ContainerWidth, UI_Container } from 'ui/components/4-habitats/Container/Container';
import { useRouteContext } from 'ui/contexts/RouteContext';
import classNames from 'classnames';
import { UI_Dropdown } from 'ui/components/1-atoms/Form/Dropdown/Dropdown';

export interface TabsProps {
	selectedId?: string;
	tabsTitle: string;
	tabs: {
		title: string;
		id: string;
		content: React.ReactNode;
	}[];
	hideTitle?: boolean;
	width: ContainerWidth;
	removeTopSpacing?: boolean;
}

export const UI_Tabs = ({ tabsTitle, tabs, selectedId, hideTitle, width, removeTopSpacing }: TabsProps) => {
	const [selectedTabId, setSelectedTabId] = useState(selectedId);
	const { router } = useRouteContext() ?? {};

	useEffect(() => {
		if (!router) return;
		const hashId = router.asPath.split('#')[1];

		const isValidTabId = (id?: string) => tabs.some((tab) => tab.id === id);

		const validHashId = isValidTabId(hashId) ? hashId : undefined;
		const validSelectedId = isValidTabId(selectedId) ? selectedId : undefined;

		const idToUse = validHashId || validSelectedId || tabs[0]?.id;
		setSelectedTabId(idToUse);

		const handleHashChange = (url: string) => {
			const newHashId = url.split('#')[1];
			if (isValidTabId(newHashId)) {
				setSelectedTabId(newHashId);
			}
		};

		router.events.on('hashChangeComplete', handleHashChange);
		return () => router.events.off('hashChangeComplete', handleHashChange);
	}, [selectedId, tabs, router]);

	useEffect(() => {
		if (selectedTabId) {
			// Forces picture resize eventListener to trigger and get correct size
			window.dispatchEvent(new Event('resize'));
		}
	}, [selectedTabId]);

	return (
		<div className={classNames(styles.Tabs, removeTopSpacing ? styles.Tabs___noTopSpacing : '')}>
			<UI_Container width={width}>
				<nav className={styles.Tabs_navigation}>
					<UI_Dropdown
						id="tabs-dropdown"
						label={hideTitle ? undefined : tabsTitle}
						options={tabs.map((tab) => ({ value: tab.id, label: tab.title }))}
						value={selectedTabId}
						onChange={setSelectedTabId}
					/>
				</nav>

				{tabs.map((tabItem) => (
					<div
						className={classNames(styles.Tabs_itemContent, selectedTabId !== tabItem.id && styles.Tabs_itemContent___hidden)}
						key={tabItem.id + '-content'}
						aria-hidden={selectedTabId !== tabItem.id}
					>
						{tabItem.content}
					</div>
				))}
			</UI_Container>
		</div>
	);
};
